<template>
    <div class="content-wrapper">
      <!-- Content Header (Page header) -->
    <section class="content-header">
      <h1>
       {{actionToBePerformed}}
      </h1>
      <ol class="breadcrumb">
        <li><a href="#"><i class="fa fa-dashboard"></i> Home</a></li>
        <li><router-link to="/prompt-management/view">Back</router-link></li>        
        <li class="active">Prompt Management</li>
      </ol>
    </section>

    <section class="content">
        <div class="row">
        <div class="col-xs-12">
          <div class="box">
            <div class="box-body">
               <div class="row equal-height">  
                  <!-- Create two parts of the div. 
                    1) Form in the right side which takes prompt_name, owner, description as an input
                    2) Chat window in the right side where user can chat with the bot
                    Users can modify the prompt and then chat with the bot.
                    -->
                  
                  <!-- first part of the div -->
                  <div class="col-xs-12 col-md-6">
                    <form @submit.prevent="createOrUpdatePrompt" action="#"  method="post">
                    <div class="panel panel-default">
                      <div class="sandbox-panel-heading">
                        <h3 class="panel-title">Prompt Configuration</h3>
                        <nv-select class="sandbox-version-select" v-model="selectedPromptVersion" @change="onVersionChange" placeholder="Version">
                          <option v-for="version in promptVersions" :key="version" :value="version">
                            {{ version }}
                          </option>
                        </nv-select>
                      </div>
                      <div class="panel-body">
                        <div class="form-group">
                          <label for="prompt_name">Prompt Name</label>
                          <input
                            type="text"
                            class="form-control"
                            v-model.trim="promptData.prompt_name"
                            id="prompt_name"
                            placeholder="Enter Prompt Name"                            
                          />
                          <span
                            v-show="$v.promptData.prompt_name.$error && $v.promptData.prompt_name.$dirty"
                            class="text-danger"
                            >Prompt Name is required</span
                          >
                        </div>
                        <div class="form-group">
                          <label for="prompt">Actual Prompt
                            (<span @click="toggleGenAIOnboardingVariable" style="cursor: pointer; color: #1a73e8; text-decoration: underline;">{{ showGenAIOnboardingVairableToggle ? 'Hide' : 'Show' }} Onboarding Variables)</span>
                            (<span @click="toggleNlpIntentDetectionVariable" style="cursor: pointer; color: #1a73e8; text-decoration: underline;">{{ showNLPIntentDetectionVariableToggle ? 'Hide' : 'Show' }}  NLP Intent Detection Variables</span>)
                          </label>
                            <div v-if="showNLPIntentDetectionVariableToggle">
                              <p><strong>NLP Intent Detection Variables: </strong>You can use these variables in the prompt. They will get replaced with values at the time of execution:</p>
                              <ul>
                                <li v-for="variable in dynamicNlpVariables" :key="variable.name">
                                  <strong>{{ '<<' + variable.name + '>>' }}</strong> - {{ variable.description }}
                                </li>
                              </ul>
                            </div>
                            <div v-if="showGenAIOnboardingVairableToggle">
                              <p><strong>GenAI Onboarding Variables: </strong>You can use these variables in the prompt. They will get replaced with values at the time of execution:</p>
                              <ul>
                                <li v-for="variable in dynamicOnboardingVariables" :key="variable.name">
                                  <strong>{{ '<<' + variable.name + '>>' }}</strong> - {{ variable.description }}
                                </li>
                              </ul>
                            </div>
                          <textarea
                            class="form-control"
                            v-model.trim="promptData.prompt"
                            id="prompt"
                            rows="4"
                            placeholder="Enter Prompt"
                          ></textarea>
                          <span
                            v-show="$v.promptData.prompt.$error && $v.promptData.prompt.$dirty"
                            class="text-danger"
                            >Prompt is required</span
                          >
                        </div>
                        <div class="form-group">
                          <label for="description">Description</label>
                          <textarea
                            class="form-control"
                            v-model.trim="promptData.description"
                            id="description"
                            placeholder="Enter Description"
                          ></textarea>
                          <span
                            v-show="$v.promptData.description.$error && $v.promptData.description.$dirty"
                            class="text-danger"
                            >Prompt description is required</span
                          >
                        </div>

                        <!-- New LLM Configuration Section -->
                        <div class="form-group">
                            <div class="row">
                              <div class="col-xs-12">
                                <label for="modelVersion">LLM Model Version</label>
                                <nv-select
                                  class="form-control"
                                  v-model="promptData.configuration.modelVersion"
                                  id="modelVersion"
                                >
                                  <option value="">Select Model Version</option>
                                  <option v-for="model in modelVersions" :key="model.value" :value="model.value">
                                    {{ model.label }}
                                  </option>
                                </nv-select>
                                <span
                                  v-show="$v.promptData.configuration.modelVersion.$error"
                                  class="text-danger"
                                >Model version is required</span>
                              </div>
                            </div>
                          <div class="llm-config-container">
                            <div class="row">
                              <div class="col-xs-6">
                                <label for="temperature">Temperature</label>
                                <input
                                  type="number"
                                  class="form-control"
                                  v-model.number="promptData.configuration.temperature"
                                  id="temperature"
                                  step="0.01"
                                  min="0"
                                  max="2"
                                  placeholder="Enter Temperature (0-2)"
                                />
                                <span
                                  v-show="$v.promptData.configuration.temperature.$error"
                                  class="text-danger"
                                >Temperature must be between 0 and 1</span>
                              </div>
                              <div class="col-xs-6">
                                <label for="topP">Top P</label>
                                <input
                                  type="number"
                                  class="form-control"
                                  v-model.number="promptData.configuration.topP"
                                  id="topP"
                                  step="0.01"
                                  min="0"
                                  max="1"
                                  placeholder="Enter Top P (0-1)"
                                />
                                <span
                                  v-show="$v.promptData.configuration.topP.$error"
                                  class="text-danger"
                                >Top P must be between 0 and 1</span>
                              </div>
                            </div>
                            <div class="row margin-top-10">
                              <div class="col-xs-6">
                                <label for="topK">Top K</label>
                                <input
                                  type="number"
                                  class="form-control"
                                  v-model.number="promptData.configuration.topK"
                                  id="topK"
                                  min="1"
                                  step="1"
                                  placeholder="Enter Top K"
                                />
                                <span
                                  v-show="$v.promptData.configuration.topK.$error"
                                  class="text-danger"
                                >Top K must be a positive integer</span>
                              </div>
                              <div class="col-xs-6">
                                <label for="maxOutputTokens">Max Output Tokens</label>
                                <input
                                  type="number"
                                  class="form-control"
                                  v-model.number="promptData.configuration.maxOutputTokens"
                                  id="maxOutputTokens"
                                  min="1"
                                  step="1"
                                  placeholder="Enter Max Output Tokens"
                                />
                                <span
                                  v-show="$v.promptData.configuration.maxOutputTokens.$error"
                                  class="text-danger"
                                >Max Output Tokens must be a positive integer</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <!-- End of LLM Configuration Section -->

                        <div class="form-group">
                          <label for="owner">Owner</label>
                          <input
                            type="text"
                            class="form-control"
                            v-model.trim="promptData.owner"
                            disabled
                            id="owner"
                            placeholder="Enter Owner Name"
                          />
                        </div>
                        <div class="form-group">
                          <label for="age">Age</label>
                          <input
                            type="number"
                            class="form-control"
                            v-model.trim="promptData.age"
                            id="age"
                            placeholder="Enter Age"
                          />
                        </div>
                        <div class="form-group">
                          <label for="gender">Gender</label>
                          <nv-select
                            class="form-control"
                            v-model.trim="promptData.gender"
                            id="gender"
                          >
                            <option value="">Select Gender</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                            <option value="other">Other</option>
                          </nv-select>
                        </div>
                        <div class="form-group">
                          <label for="location">Location</label>
                          <input
                            type="text"
                            class="form-control"
                            v-model.trim="promptData.location"
                            id="location"
                            placeholder="Enter Location"
                          />
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="action-button">
                        <label style="font-weight: normal; cursor: pointer;">
                          <input type="checkbox" v-model="invalidateCache" />
                          Invalidate cache on save
                        </label>
                      </div>
                    </div>
                    <div class="row">
                    <div class="action-button">
                      <button type="button" class="btn btn-primary" @click="close">Cancel</button>
                      <input type="submit" class="btn btn-primary margin-left-20px" value="Submit">
                    </div>
                  </div>
                    </form>
                  </div>
                  
                  <!-- Right Side: Chat Window -->
                <div class="col-xs-12 col-md-6">
                  <div class="chat-window">
                    <div class="load-more-container">
                      <button  @click="loadMoreMessages" class="load-more-button">  
                        <i v-if="loadingMoreMessages" class="fa fa-spinner fa-spin"></i>
                        <span v-else><i class="fa fa-history"></i> Load more</span>
                      </button>
                      <!-- <button @click="clearChatHistory" class="forget-chat-history"> 
                        <i v-if="clearingHistory" class="fa fa-spinner fa-spin"></i>
                        <span v-else>Forget Chat History</span>
                      </button> -->
                      <button @click="showCreateSessionModal = true" class="create-session-button"><i class="fa fa-plus"></i> New session</button>
                      <nv-select v-if="selectedSession" class="session-select" v-model="selectedSession" @change="onSessionChange" placeholder="Select Session">
                          <option v-for="session in sessions" :key="session.id" :value="session.id">
                            {{ session.session_name }}
                          </option>
                      </nv-select>
                      <button @click="exportChat" class="create-session-button"><i class="fa fa-download"></i> Export</button>
                    </div>
                    <div class="messages">
                      <div v-for="(message, index) in messages" :key="index" class="sandbox-message">
                        <div v-if="message.type === 'user'" class="user-message chat-bubble--right">
                          <div class="message-sent-by">You</div>
                          {{ message.text }}
                        </div>
                        <div v-if="message.type === 'api'" class="chat-bubble chat-bubble--left"> 
                        <div class="message-sent-by">Bot</div>
                        {{ message.text }}
                        </div>
                      </div>
                    </div>
                    <div class="input-box-container">
                      <form @submit.prevent="sendMessage" action="#"  method="post" class="chatbot-form">
                      <input v-model="newMessage" type="text" class="input-box" placeholder="Type a message" :disabled="sendingMessage" />
                        <button type="submit" class="send-button" :disabled="sendingMessage">
                          <i v-if="sendingMessage" class="fa fa-spinner fa-spin"></i>
                          <i v-else class="fa fa-paper-plane"></i>
                        </button>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div> 
        </div>
      </div>

    </section>
    <CreateSandboxSession 
      v-if="showCreateSessionModal" 
      @close="showCreateSessionModal = false"
      @submitSession="handleSessionSubmit"
    />
    <BlockUI v-if="loading"><i class="fa fa-cog fa-spin fa-3x fa-fw"></i><br/>Please wait...</BlockUI>
    </div>
</template>
<script>
  import { required, between, integer, minValue } from 'vuelidate/lib/validators'
  import api from '../../../utils/api'
  import { llmModelVersions, defaultPromptConfiguration } from './constants'
  import CreateSandboxSession from './../popup/createSandboxSession.vue'
  export default {
    name: 'PromptDetails',
    components: {
      CreateSandboxSession
    },
    validations: {
      promptData: {
        prompt_name: { required },
        description: { required },
        prompt: { required },
        configuration: {
          modelVersion: { required },
          temperature: { required, between: between(0, 2) },
          topP: { required, between: between(0, 1) },
          topK: { required, integer, minValue: minValue(1) },
          maxOutputTokens: { required, integer, minValue: minValue(1) }
        }
      }
    },
    data () {
      return {
        modelVersions: llmModelVersions,
        promptVersions: [],
        selectedPromptVersion: '',
        sessions: [],
        selectedSession: null,
        newMessage: '', // For the new user message
        messages: [], // To store the chat history
        showCreateSessionModal: false,
        sessionName: '',
        promptData: {
          id: null,
          prompt_name: '',
          prompt: '',
          description: '',
          owner: localStorage.getItem('email'),
          age: null,
          gender: '',
          location: null,
          configuration: {
            modelVersion: defaultPromptConfiguration.modelVersion, // Default to gemini 2.0 flash
            temperature: defaultPromptConfiguration.temperature,
            topP: defaultPromptConfiguration.topP,
            topK: defaultPromptConfiguration.topK,
            maxOutputTokens: defaultPromptConfiguration.maxOutputTokens
          }
        },
        currentPage: 1, // Track the current page number
        pageSize: 10, // Define the page size
        loading: false,
        sendingMessage: false,
        clearingHistory: false,
        loadingMoreMessages: false,
        isEdit: false,
        invalidAttributes: false,
        gRowObj: {
          id: null,
          media_name: '',
          media_type: null
        },
        submitted: false,
        actionToBePerformed: 'Create Prompt',
        isEdited: false,
        index: -1,
        invalidateCache: true,
        showNLPIntentDetectionVariableToggle: false,
        dynamicNlpVariables: [
          { name: 'AGE', description: 'User\'s age' },
          { name: 'GENDER', description: 'User\'s gender' },
          { name: 'LOCATION', description: 'User\'s location' },
          { name: 'LANGUAGE', description: 'User language' },
          { name: 'COUNTRY', description: 'User\'s country' },
          { name: 'TOPIC', description: 'Selected NLP topic' },
          { name: 'USERINPUT', description: 'Text entered by user' },
          { name: 'QUESTION', description: 'Bot Question for which user sent message' },
          { name: 'LABELS', description: 'List of labels for NLP Topic in comma-separated format' },
          { name: 'LABELS_NUMBERED', description: 'List of labels for NLP Topic in Numbered format' },
          { name: 'LABELS_BULLETED', description: 'List of labels for NLP Topic in Bulleted format' },
          { name: 'RECOMMENDATIONS', description: 'List of recommendations set for the user in comma-separated format' },
          { name: 'RECOMMENDATIONS_NUMBERED', description: 'List of recommendations set for the user in Numbered format' },
          { name: 'RECOMMENDATIONS_BULLETED', description: 'List of recommendations set for the user in Bulleted format' },
          { name: 'REFERRALS', description: 'List of referrals given in comma-separated format' },
          { name: 'REFERRALS_NUMBERED', description: 'List of referrals given in Numbered format' },
          { name: 'REFERRALS_BULLETED', description: 'List of referrals given in Bulleted format' }
        ],
        showGenAIOnboardingVairableToggle: false,
        dynamicOnboardingVariables: [
          { name: 'AD_CONTEXT', description: 'Details about the ad' },
          { name: 'COUNTRY', description: 'User\'s country' },
          { name: 'DEFAULT_LANGUAGE', description: 'Default language for welcome message' },
          { name: 'DEFAULT_LOCATION', description: 'Default location for the user reached via keyword' },
          { name: 'DEFAULT_GENDER', description: 'Default gender to set for the user' },
          { name: 'UNDERAGE_LIMIT', description: 'Minimum age required for onboarding' },
          { name: 'AUTO_ACCEPT_TNC', description: 'Whether to ask the user to agree or disagree to the T&C' }
        ]
      }
    },
    methods: {
      async exportChat () {
        try {
          const response = await api.buildRequest('exportChatSession', 'POST', {
            sessionId: this.selectedSession
          }, undefined, 'arraybuffer')
          console.log('Export chat response:', response)
          const contentDisposition = response.headers['content-disposition']
          const fileNameMatch = contentDisposition && contentDisposition.match(/filename=(.*)/)
          const fileName = fileNameMatch ? fileNameMatch[1] : 'file.xlsx'
          console.log('response.data', response.data)
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', fileName)
          document.body.appendChild(link)
          link.click()
        } catch (error) {
          console.error('Error exporting chat:', error)
          this.$swal({
            type: 'error',
            text: 'An error occurred'
          })
        }
      },
      async onSessionChange () {
        try {
          console.log('Clicked On Session Change', this.selectedSession)
          this.loading = true
          this.currentPage = 1
          this.pageSize = 10
          this.messages = []
          const promptVersionOfSelectedSession = this.sessions.find(session => session.id === this.selectedSession).prompt_version
          console.log('promptVersionOfSelectedSession:', promptVersionOfSelectedSession)
          // Get Chat History and get prompt details
          const promiseArray = [this.getPromptDetailsByVersion(this.$route.query.promptId, promptVersionOfSelectedSession), this.fetchChatHistory(this.$route.query.promptId, localStorage.getItem('email'), this.selectedSession)]
          await Promise.all(promiseArray)
          this.loading = false
        } catch (error) {
          console.log(error)
          this.$swal({
            type: 'error',
            text: 'An error occurred'
          })
        }
      },
      async handleSessionSubmit (sessionName) {
        try {
          this.loading = true
          const params = {
            sessionName,
            promptId: this.$route.query.promptId,
            promptVersion: this.selectedPromptVersion,
            owner: localStorage.getItem('email')
          }
          const response = await api.buildRequest('createSandboxSession', 'POST', params)
          await this.getSessionList(this.$route.query.promptId)
          this.selectedSession = response.data.payload.data.id
          this.messages = []
          this.newMessage = ''
          this.showCreateSessionModal = false
          this.loading = false
        } catch (error) {
          console.error('Error creating session:', error)
          this.loading = false
        }
      },
      async onVersionChange () {
        this.loading = true
        console.log('Selected version:', this.selectedPromptVersion)
        await this.getPromptDetailsByVersion(this.$route.query.promptId, this.selectedPromptVersion)
        this.loading = false
      },
      async getPromptDetailsByVersion (promptId, version) {
        return new Promise(resolve => {
          api.buildRequest('getPromptDetailsByVersion', 'POST', {promptId, version}).then(async (response) => {
            const data = (response && response.data && response.data.payload && response.data.payload.data) ? response.data.payload.data : {}
            if (data && Object.keys(data).length > 0) {
              console.log('Data from getPromptDetails: ', JSON.stringify(data))
              this.promptData.id = data.prompt_id
              this.promptData.prompt_name = data.prompt_name
              this.promptData.prompt = data.prompt
              this.promptData.description = data.description
              this.promptData.owner = data.created_by
              this.promptData.age = data.prompt_metadata.age
              this.promptData.gender = data.prompt_metadata.gender || ''
              this.promptData.location = data.prompt_metadata.location
              this.selectedPromptVersion = data.version
            }
            this.promptData.configuration = {
              temperature: data.configuration.temperature || defaultPromptConfiguration.temperature,
              topP: data.configuration.topP || defaultPromptConfiguration.topP,
              topK: data.configuration.topK || defaultPromptConfiguration.topK,
              maxOutputTokens: data.configuration.maxOutputTokens || defaultPromptConfiguration.maxOutputTokens,
              modelVersion: data.configuration.modelVersion || defaultPromptConfiguration.modelVersion
            }
            resolve(true)
          }).catch((error) => {
            console.log('Error in getPromptDetails')
            console.log(error)
            resolve(true)
            this.$swal({
              type: 'error',
              text: 'An error occurred'
            })
          })
        })
      },
      sendMessage () {
          // Check if the message is not empty
        this.sendingMessage = true
        if (this.newMessage.trim()) {
          console.log('## this is form data: ', this.promptData)
            // Add user message to chat window
          this.messages.push({ type: 'user', text: this.newMessage })
          this.scrollToBottom()
          const messageDetails = { message: this.newMessage, ...this.promptData, msgFrom: localStorage.getItem('email'), sessionId: this.selectedSession, promptVersion: this.selectedPromptVersion }
          console.log('selectedSession: ', this.selectedSession)
          console.log('selectedPromptVersion in sendmessage', this.selectedPromptVersion)
          return new Promise(resolve => {
            api.buildRequest('sendMessage', 'POST', messageDetails).then(async (response) => {
              console.log('APIRESP FROM SEND MSG: ', response.data.payload)
              console.log('##message response:', response.data.payload.response)
              const apiResponse = (response && response.data && response.data.payload) ? response.data.payload.response : 'Sorry, I am not able to understand your query. Please try again.'
              console.log('apiResponse: ', apiResponse)
              // Add the API response to the chat window
              this.messages.push({ type: 'api', text: apiResponse })
              this.newMessage = ''
              this.scrollToBottom()
              resolve(true)
            }).catch((error) => {
              console.log('Error in getPromptDetails')
              console.log(error)
              resolve(true)
              this.$swal({
                type: 'error',
                text: 'An error occurred'
              })
            }).finally(() => {
              this.sendingMessage = false
            })
          })
        }
      },
      scrollToBottom () {
        this.$nextTick(() => {
          const messages = this.$el.querySelector('.messages')
          messages.scrollTop = messages.scrollHeight
        })
      },
      close () {
        this.$router.replace('/prompt-management/view')
      },
      toggleNlpIntentDetectionVariable () {
        this.showGenAIOnboardingVairableToggle = false
        this.showNLPIntentDetectionVariableToggle = !this.showNLPIntentDetectionVariableToggle
      },
      toggleGenAIOnboardingVariable () {
        this.showNLPIntentDetectionVariableToggle = false
        this.showGenAIOnboardingVairableToggle = !this.showGenAIOnboardingVairableToggle
      },
      getByValue (map, searchValue) {
        for (let key of Object.keys(map)) {
          if (searchValue === map[key]) {
            return key
          }
        }
      },
      async getPromptDetails (id, selectedPromptVersion) {
        const param = {
          id: id
        }
        console.log('Prompt id: ', id)
        console.log('Selected version: ', selectedPromptVersion)
        return new Promise(resolve => {
          api.buildRequest('getPromptDetails', 'POST', param).then(async (response) => {
            const data = (response && response.data && response.data.payload && response.data.payload.data) ? response.data.payload.data : {}
            if (data && Object.keys(data).length > 0) {
              this.promptData.id = data.id
              this.promptData.prompt_name = data.prompt_name
              this.promptData.prompt = data.prompt
              this.promptData.description = data.description
              this.promptData.owner = data.created_by
              this.promptData.age = data.prompt_metadata.age
              this.promptData.gender = data.prompt_metadata.gender || ''
              this.promptData.location = data.prompt_metadata.location
              this.selectedPromptVersion = data.version

              this.promptData.configuration = {
                temperature: (data.configuration && data.configuration.temperature) || defaultPromptConfiguration.temperature,
                topP: (data.configuration && data.configuration.topP) || defaultPromptConfiguration.topP,
                topK: (data.configuration && data.configuration.topK) || defaultPromptConfiguration.topK,
                maxOutputTokens: (data.configuration && data.configuration.maxOutputTokens) || defaultPromptConfiguration.maxOutputTokens,
                modelVersion: (data.configuration && data.configuration.modelVersion) || defaultPromptConfiguration.modelVersion
              }
            }
            console.log('##Prompt Data:', this.promptData)
            console.log('this.selectedPromptVersion = data.version', this.selectedPromptVersion)
            resolve(true)
          }).catch((error) => {
            console.log('Error in getPromptDetails')
            console.log(error)
            resolve(true)
            this.$swal({
              type: 'error',
              text: 'An error occurred'
            })
          })
        })
      },
      async createOrUpdatePrompt () {
        console.log('######## PROMPT SUBMITTED ######')
        this.submitted = true
        this.$v.promptData.$touch()
        console.log('###:', this.$v.promptData.attributes)
        console.log('###:', this.$v.promptData.$invalid)
        if (this.$v.promptData.$invalid || (this.promptData.attributes && this.invalidAttributes)) {
          return
        }
        console.log('Cleared validations...')
        this.loading = true
        const promptDetails = {
          prompt_name: this.promptData.prompt_name,
          prompt: this.promptData.prompt,
          description: this.promptData.description,
          created_by: this.promptData.owner,
          prompt_metadata: {
            age: this.promptData.age,
            gender: this.promptData.gender || '',
            location: this.promptData.location
          },
          configuration: this.promptData.configuration
        }
        if (this.promptData.id) {
          // -- call edit
          promptDetails.id = this.promptData.id
          return new Promise(resolve => {
            api.buildRequest('updatePrompt', 'POST', promptDetails).then(async () => {
              console.log('Update prompt body: ', promptDetails)
              this.loading = false
              this.$swal({
                type: 'success',
                text: 'Prompt updated successfully'
              })
              try {
                await api.buildRequest('llm-cache/invalidate', 'POST', {
                  type: 'prompt',
                  prompt_id: this.promptData.id,
                  invalidate: this.invalidateCache
                })
              } catch (err) {
                console.warn('Cache invalidation failed:', err.message)
              }
              resolve()
            }).catch(({ response }) => {
              console.log('Error in createupdateprompt')
              console.log(response)
              resolve(true)
              this.loading = false
              this.$swal({
                type: 'error',
                text: response ? response.data.payload.message : 'An error occurred'
              })
            })
          })
        } else {
          // -- call create
          return new Promise(resolve => {
            console.log('Create prompt body: ', promptDetails)
            api.buildRequest('createPrompt', 'POST', promptDetails).then(async (response) => {
              this.loading = false
              this.$swal({
                type: 'success',
                text: 'Prompt updated successfully'
              })
              const createdId = response && response.data && response.data.payload && response.data.payload.data && response.data.payload.data.id
              try {
                await api.buildRequest('llm-cache/invalidate', 'POST', {
                  type: 'prompt',
                  prompt_id: createdId || this.promptData.id,
                  invalidate: this.invalidateCache
                })
              } catch (err) {
                console.warn('Cache invalidation failed:', err.message)
              }
              resolve()
            }).catch(({ response }) => {
              console.log('Error in createupdateprompt2')
              console.log(response)
              resolve(true)
              this.loading = false
              this.$swal({
                type: 'error',
                text: response ? response.data.payload.message : 'An error occurred'
              })
            })
          })
        }
      },
      async fetchChatHistory (promptId, owner, sessionId) {
        try {
          const response = await api.buildRequest('getChatHistory', 'POST', {
            promptId,  // Pass the necessary params
            owner,
            page: this.currentPage,
            pageSize: this.pageSize,
            sessionId,
            promptVersion: this.selectedPromptVersion
          })
          // Assuming response.data is an array of chat logs
          console.log(response.data.payload.messages)
          const messagesFromDatabase = (response && response.data && response.data.payload && response.data.payload.messages) || []
          console.log('Messages from database:', messagesFromDatabase)

          if (messagesFromDatabase.length > 0) {
            const formattedMessages = messagesFromDatabase.map(message => {
              return {
                type: message.from === owner ? 'user' : 'api',
                text: message.msg
              }
            }).reverse()
            this.messages = [...formattedMessages, ...this.messages] // Append new chats to the current chatHistory
            this.currentPage += 1 // Increment the page for next load
          }
        } catch (error) {
          console.error('Error fetching chat history:', error)
        }
      },
      async loadMoreMessages () {
        this.loadingMoreMessages = true
        await this.fetchChatHistory(this.$route.query.promptId, localStorage.getItem('email'), this.selectedSession)
        this.loadingMoreMessages = false
      },
      async clearChatHistory () {
        this.clearingHistory = true
        this.messages = []
        this.currentPage = 1
        await api.buildRequest('archiveChatHistory', 'POST', {
          promptId: this.$route.query.promptId,
          owner: localStorage.getItem('email')
        })
        this.clearingHistory = false
      },
      async getVersionList (promptId) {
        return new Promise(resolve => {
          api.buildRequest('prompt-versions', 'POST', { promptId }).then((response) => {
            console.log('Version list:', response.data.payload)
            this.promptVersions = response.data && response.data.payload && response.data.payload.versions || ['V1.0.0']
            resolve(true)
          }).catch((error) => {
            console.log('Error in getVersionList')
            console.log(error)
            resolve(true)
            this.$swal({
              type: 'error',
              text: 'An error occurred'
            })
          })
        })
      },
      async getSessionList (promptId) {
        return new Promise(resolve => {
          api.buildRequest('sessionList', 'POST', { promptId }).then((response) => {
            console.log('Session list:', response.data.payload)
            this.sessions = response.data && response.data.payload && response.data.payload.sessions || []
            console.log('this.sessions:', this.sessions)
            if (this.sessions && this.sessions.length) {
              this.selectedSession = this.sessions[0].id
            }
            console.log('this.selectedSession:', this.selectedSession)
            resolve(true)
          }).catch((error) => {
            console.log('Error in getSessionList')
            console.log(error)
            resolve(true)
            this.$swal({
              type: 'error',
              text: 'An error occurred'
            })
          })
        })
      }
    },
    computed: {
      acceptTypes () {
        if (!this.gRowObj.media_type) {
          return null
        }

        const mimeToAccept = {
          'video/3gp': '.3gp,video/3gpp',
          'audio/mpeg': '.mp3,audio/mpeg'
        }

        return mimeToAccept[this.gRowObj.media_type] || this.gRowObj.media_type
      }
    },
    async mounted () {
      this.loading = true
      this.gRowObj = {
        id: null,
        media_name: '',
        media_type: null
      }
      const promiseArray = []

      if (this.$route && this.$route.query && this.$route.query.promptId) {
        this.actionToBePerformed = 'Update Prompt'
        this.isEdit = true
        await this.getPromptDetails(this.$route.query.promptId)
      }
      if (this.$route.query.promptId) {
        // For first time when chat history is fetched we fetch recent chat data, assuming that is always from latest session
        promiseArray.push(this.fetchChatHistory(this.$route.query.promptId, localStorage.getItem('email')))
        promiseArray.push(this.getVersionList(this.$route.query.promptId))
        promiseArray.push(this.getSessionList(this.$route.query.promptId))
      }
      await Promise.all(promiseArray)
      this.scrollToBottom()
      this.loading = false
    }
  }
</script>
<style>
.sandbox-panel-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  background: #F5F7FA;
  border-bottom: 1px solid #E2E8F0;
  border-radius: 11px 11px 0 0;
}
.sandbox-panel-heading .panel-title {
  flex-shrink: 0;
  font-size: 16px;
  font-weight: 700;
  color: #1E3A5F;
}
.sandbox-version-select {
  width: 180px;
}

.margin-top-20 {
  margin-top: 20px;
}
.width-100 {
  min-width: 100px;
}

.equal-height {
  display: flex;
}

.equal-height > .col-md-6 {
  display: flex;
  flex-direction: column;
}

/* Sandbox chat */
.chat-window {
  background-color: #F5F7FA;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  height: 820px;
}
.messages {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  background-color: #F5F7FA;
}
.input-box-container {
  display: flex;
  padding: 12px;
  background-color: #FFFFFF;
  border-top: 1px solid #E2E8F0;
}
.chatbot-form {
  display: flex;
  flex: 1;
  gap: 8px;
}
.input-box {
  flex: 1;
  height: 40px;
  padding: 0 14px;
  border: 1px solid #CFD8E3;
  border-radius: 9999px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.input-box:focus {
  border-color: #0D9488;
  box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.18);
}
.input-box[disabled] {
  background-color: #F5F7FA;
  color: #8896A5;
  cursor: not-allowed;
}
.send-button {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  background-color: #0D9488;
  color: #FFFFFF;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  font-size: 15px;
  transition: background-color 0.15s ease;
}
.send-button:hover {
  background-color: #0B7C72;
}
.send-button[disabled] {
  background-color: #CFD8E3;
  cursor: not-allowed;
}

.sandbox-message {
  margin-bottom: 12px;
  display: flex;
}
.message-sent-by {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: 2px;
  opacity: 0.75;
}
.message-time {
  font-size: 12px;
  color: #8896A5;
  margin-top: 5px;
}
.chat-bubble,
.user-message {
  padding: 10px 14px;
  max-width: 70%;
  word-wrap: break-word;
  white-space: pre-wrap;
  line-height: 1.45;
  animation: fadeIn 0.3s ease-in;
}
.chat-bubble--left {
  background: #FFFFFF;
  color: #2D3748;
  border: 1px solid #E2E8F0;
  border-radius: 14px 14px 14px 4px;
  box-shadow: 0 1px 3px rgba(30, 58, 95, 0.06);
}
.chat-bubble--left .message-sent-by { color: #0B7C72; }
.user-message,
.chat-bubble--right {
  margin-left: auto;
  background: #1E3A5F;
  color: #FFFFFF;
  border-radius: 14px 14px 4px 14px;
}
.user-message .message-sent-by { color: #FFFFFF; }

/* Toolbar above the chat */
.load-more-container {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background-color: #FFFFFF;
  border-bottom: 1px solid #E2E8F0;
}
.load-more-button,
.create-session-button,
.forget-chat-history {
  height: 36px;
  padding: 0 14px;
  flex-shrink: 0;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.load-more-button .fa,
.create-session-button .fa { margin-right: 4px; }
.load-more-button {
  background-color: #FFFFFF;
  color: #1E3A5F;
  border: 1px solid #CFD8E3;
}
.load-more-button:hover {
  background-color: #F5F7FA;
}
.create-session-button {
  background-color: #E6F5F4;
  color: #0B7C72;
  border: 1px solid #E6F5F4;
}
.create-session-button:hover {
  background-color: #CDEBE8;
  border-color: #CDEBE8;
}
.forget-chat-history {
  background-color: #FCE8EB;
  color: #C94455;
  border: 1px solid #FCE8EB;
}
.forget-chat-history:hover {
  background-color: #F8D3D9;
}
.session-select {
  flex: 1;
  min-width: 0;
}

.llm-config-container {
  margin-top: 10px;
}

</style>



// WEBPACK FOOTER //
// createOrUpdatePrompt.vue?29629e98